<script setup>

import { computed, ref, watch } from 'vue'
import { getI18n } from '@/plugins/i18n'

import { DxDataGrid, DxEditing, DxSelection, DxScrolling, DxFilterRow } from 'devextreme-vue/data-grid'

import { useODataSource, useCurrencyColumnFormatter, useDefaultGridOptions, useLookup } from '@/composables/data-grid'
import { useNotify } from '@/composables/notify'
import { useRegistryStore } from '@/stores/registry.store'
import ChipStatus from '@/components/shared/ChipStatus.vue'

const i18n = getI18n()
const { notifyWarning } = useNotify()
const registryStore = useRegistryStore()

const supersessionDataSource = ref([])
const supersessionId = ref(null)

const supersessionProductGridColumns = computed(() =>
  [
    ...(props.showTieType ? [{
      dataField: 'tie_type',
      caption: i18n.t('common.tieTypology'),
      width: 160,
      alignment: 'center',
      cellTemplate: 'tieTypeCellTemplate',
      calculateCellValue: () => registryStore.productTieTypes.SUPERSESSION,
      lookup: useLookup({ store: registryStore.getLocalizedProductTieTypes(), displayExpr: 'name' }),
      allowSorting: false,
      allowFiltering: false,
    }] : []),
    { dataField: 'level', caption: i18n.t('common.level'), width: 70, dataType: 'number', allowSorting: false, allowFiltering: false },
    { dataField: 'product_code', caption: i18n.t('common.code'), width: 180, allowSorting: false, allowFiltering: false },
    {
      dataField: 'product_name', caption: i18n.t('common.name'),
      minWidth: 300,
      encodeHtml: false,
      calculateDisplayValue: (rowData) => { return `${'&nbsp;'.repeat(rowData.level * 5)}${rowData.product_name || ''}` },
      allowSorting: false, allowFiltering: false
    },
    {
      dataField: 'product_availability', caption: i18n.t('common.availability'), width: 160, allowSorting: false, allowFiltering: false,
      calculateCellValue: (rowData) => registryStore.getLocalizedProductAvailabilities().find(i => i.id === rowData.product_availability)?.name || ''
    },
    { dataField: 'quantity', caption: i18n.t('common.supersessionQuantityAlt'), width: 100, dataType: 'number', allowSorting: false, allowFiltering: false },
    {
      dataField: 'product_price', caption: i18n.t('common.priceNoVat'), width: 150,
      dataType: 'number',
      calculateDisplayValue: (rowData) => useCurrencyColumnFormatter(rowData, 'product_price', rowData.currency),
      calculateSortValue: 'product_price',
      allowSorting: false, allowFiltering: false
    },
    { dataField: 'product_has_supersession', caption: i18n.t('common.supersession'), width: 130, dataType: 'boolean', allowSorting: false, allowFiltering: false },
  ]
)

const emit = defineEmits(['newProductSelected'])
const props = defineProps({
  data: {
    type: Object,
    default: null,
  },
  currency: {
    type: Object,
    default: null,
  },
  brand: {
    type: Object,
    default: null,
  },
  documentRowType: {
    type: Number,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  readOnly: {
    type: Boolean,
    default: false,
  },
  showTieType: {
    type: Boolean,
    default: false,
  },
  options: {
    type: Object,
    default: null
  },
})

const gridOptions = props.readOnly ? useDefaultGridOptions({
  columnWidth: undefined,
  paging: { enabled: false },
  pager: { visible: false },
  columnChooser: { enabled: false },
  height: '100%',
}) : {}

const onValueChanged = async(e) => {
  if (e) {
    if (e.has_supersession) {
      notifyWarning(i18n.t('warning.product_has_supersession'))
    }
    if (e.product_availability === registryStore.productAvailabilitiesEnum.NOT_AVAILABLE) {
      notifyWarning(i18n.t('warning.product_not_available'))
    }

    emit('newProductSelected', e)
  }
}

const onRowPrepared = (e) => {
  if (e.rowType === 'data') {
    if (e.data?.product_availability === registryStore.productAvailabilitiesEnum.NOT_AVAILABLE) {
      e.rowElement.classList.add('dx-disabled-row')
    }
    if (e.data?.product_ref === props.data?.supersession_id) {
      e.rowElement.classList.add('dx-row-focused')
    }
  }
}

const onCellPrepared = (e) => {
  if ((e.column.dataField === 'has_supersession' && e.data?.has_supersession) || (e.column.dataField === 'product_has_supersession' && e.data?.product_has_supersession)) {
    e.cellElement.classList.add('dx-supersession-cell')
  }
}

const onSupersessionRowDblClick = async(e) => {
  if (props.readOnly || !e.data) return

  e.component.clearSelection()
  e.component.selectRows([e.data.product_ref], false)

  const products = (await registryStore.api.getProductByIdAsync(e.data.product_ref))?.data
  if (products && products.length) {
    const product = {
      ...products[0],
      supersession_quantity: e.data.quantity
    }
    onValueChanged(product)
  }
}

watch(() => props.data?.supersession_id, (newId, oldId) => {
  if (newId && newId !== oldId) {
    supersessionDataSource.value = useODataSource(
      registryStore.api.getAbsoluteUrl() + registryStore.api.productSupersessionUrl(newId),
      { key: 'product_ref', avoidDuplicateDatasourceIds: true }
    )
    supersessionId.value = newId
  } else {
    supersessionDataSource.value = []
  }
}, { immediate: true })
</script>
<template>
  <div
    class="supersession-product-grid"
    :class="{ 'supersession-product-grid-read-only': props.readOnly }"
  >
    <dx-data-grid
      v-bind="gridOptions"
      :data-source="supersessionDataSource"
      width="100%"
      height="100%"
      :remote-operations="true"
      :hover-state-enabled="true"
      :columns="supersessionProductGridColumns"
      @row-prepared="onRowPrepared"
      @cell-prepared="onCellPrepared"
      @row-dbl-click="onSupersessionRowDblClick"
    >
      <dx-editing
        :allow-adding="false"
        :allow-updating="false"
        :allow-deleting="false"
      />
      <dx-filter-row :visible="true" />
      <dx-scrolling mode="virtual" />
      <dx-selection mode="single" />
      <template #tieTypeCellTemplate="{ data: cellInfo }">
        <chip-status :data="registryStore.getLocalizedProductTieTypes().find(x => x.id === cellInfo.value)" />
      </template>
    </dx-data-grid>
  </div>
</template>

<style scoped lang="scss">
@import '@/styles/quasar.variables.scss';
.supersession-product-grid {
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
}
.field-box-container {
  display: flex;
  align-items: center;
  margin-right: 1rem;
  width: 100%;

}
.field-box {
  flex-grow: 1;
}

.field-box-btn {
  margin-right: 1rem;
  color: $warning;
}

.supersession-container {
  width: 70rem;
  height: 30rem;
  padding: 1rem;
  & .dx-widget {
    width: 100% !important;
  }
}
</style>
